import { LitElement, html } from 'lit';
import { query, property, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzInputWebComponent } from '../../gzInput/component/GzInputWebComponent';
import { GzTooltip, GzTooltipWebComponent } from '../../gzTooltip';
import { copyToClipboard } from '../../../utils/htmlElement.utils';

export class GzCopyToClipboardWebComponent extends LitElement {
  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  @property({ type: String, attribute: true, reflect: true })
  confirmationMessage = '';

  @property({ type: String, reflect: true })
  tooltipText: string;

  @property({ type: Boolean, attribute: true, reflect: true })
  disabled = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  showIconOnHover = false;

  /**
   * Support for multi line clipboard message
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  multiLine = false;

  /**
   * If set, this will be the message that is copied to clipboard, otherwise defaults to component inner text
   */
  @property({ type: String, attribute: true, reflect: true })
  clipboardMessage;

  @query('gz-tooltip')
  private tooltip: GzTooltipWebComponent;

  @state()
  private tooltipShown;

  private handleMouseLeave(): void {
    clearTimeout(this.tooltipShown);
    GzTooltip.hide();
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('mouseleave', this.handleMouseLeave);
  }
  disconnectedCallback(): void {
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    super.disconnectedCallback();
  }

  private get getSlotContent() {
    if (!this.shadowRoot) {
      return;
    }

    return this.shadowRoot.querySelector('slot');
  }

  render() {
    return html`
      <div class="d-flex copy-to-clipboard-content" part="container">
        <slot></slot>

        <gz-icon-button
          @click="${this.clickHandler}"
          icon="ds-icon-copy_clipboard"
          fontSize="16"
          buttonType="square"
          buttonFill="quiet"
          buttonColor="secondary"
          ?disabled="${this.disabled}"
          class="btn-copy-to-clipboard"
          tooltipText="${this.tooltipText ? this.tooltipText : ''}"
          part="button"
        >
        </gz-icon-button>
      </div>
    `;
  }

  clickHandler(event: MouseEvent) {
    // cancel the event on multiple clicks on the element
    if (!this.shadowRoot) {
      return;
    }

    const slot = this.shadowRoot?.querySelector('slot');
    const inputs = slot
      ? (Array.from(slot.assignedNodes()).filter(child => {
          const el = child as GzInputWebComponent;
          return el.value || el.innerText;
        }) as GzInputWebComponent[])
      : [];
    const input = inputs[0] as GzInputWebComponent;

    if (!input && !this.clipboardMessage) {
      return;
    }
    if (this.clipboardMessage) {
      copyToClipboard(this.clipboardMessage, this.multiLine);
    } else if (input.value) {
      copyToClipboard(input.value, this.multiLine);
    } else {
      copyToClipboard(input.innerText, this.multiLine);
    }
    this.handleTooltip(event);
    event.stopPropagation();
  }

  handleTooltip(event) {
    GzTooltip.show(this.confirmationMessage, event);

    this.tooltipShown = setTimeout(() => {
      GzTooltip.hide();
    }, 2000);
  }
}
